import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  useCutoffProgress,
  useDtrPeriods,
} from "../../../api/queries/approvalQueries";
import { currentPeriod } from "../../../utils/dateTime";
import {
  CUTOFF_STATUS,
  fmtHours,
  periodKey,
  periodOptionLabel,
  parsePeriodKey,
} from "./dtrFormat";

/** An employee's cut-off: Open / Approved / Processed (exported). */
export const CutoffStatusBadge = ({ status }) => {
  const meta = CUTOFF_STATUS[status] || { label: status || "—", tone: "gray" };
  return (
    <span className={`status-pill status-pill--${meta.tone}`}>
      {meta.label}
    </span>
  );
};

/** "Overtime: Yes" when the day has approved overtime (replaces the old OT flag). */
export const OvertimeTag = ({ day, compact }) =>
  day?.overtimeApproved ? (
    <Link
      to="/approvals?tab=overtime&status=APPROVED"
      className="ot-tag"
      title={`Approved overtime: ${fmtHours(day.overtimeHours)} h. Open overtime requests`}
      onClick={(e) => e.stopPropagation()}
    >
      {compact ? "OT" : "Overtime: Yes"}
    </Link>
  ) : null;

/**
 * Cut-off picker. value / onChange use "year-month-cutoff" keys; when empty it
 * selects the last finished cut-off from the server. With `scope`
 * ({ companyId, branchId, assignmentType }, the page filters) each cut-off
 * shows how many of its employees are processed.
 */
export const PeriodPicker = ({ value, onChange, id = "dtr-period", scope }) => {
  const selected = parsePeriodKey(value);
  const thisYear = currentPeriod().year;
  const year = selected?.year || thisYear;
  const query = useDtrPeriods(year);
  const periods = query.data?.periods || [];
  const progressQuery = useCutoffProgress(
    {
      year,
      companyId: scope?.companyId || undefined,
      branchId: scope?.branchId || undefined,
      assignmentType: scope?.assignmentType || undefined,
    },
    { enabled: !!scope },
  );
  const progressOf = (p) =>
    (progressQuery.data?.periods || []).find(
      (x) => x.year === p.year && x.month === p.month && x.cutoff === p.cutoff,
    );

  useEffect(() => {
    if (!value && query.data?.lastFinished)
      onChange(periodKey(query.data.lastFinished), query.data.lastFinished);
  }, [value, query.data, onChange]);

  return (
    <div className="d-flex align-items-center gap-1">
      <select
        className="status-select"
        value={year}
        onChange={(e) =>
          onChange(
            `${e.target.value}-${selected?.month || 1}-${selected?.cutoff ?? 1}`,
          )
        }
        aria-label="Year"
      >
        {[thisYear + 1, thisYear, thisYear - 1, thisYear - 2].map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
      <select
        id={id}
        className="status-select dtr-period-select"
        value={value}
        onChange={(e) =>
          onChange(
            e.target.value,
            periods.find((p) => periodKey(p) === e.target.value),
          )
        }
        aria-label="Cut-off period"
        disabled={query.isLoading}
      >
        {!periods.some((p) => periodKey(p) === value) && (
          <option value={value}>
            {query.isLoading ? "Loading…" : "Select cut-off"}
          </option>
        )}
        {periods.map((p) => (
          <option key={periodKey(p)} value={periodKey(p)}>
            {periodOptionLabel(
              p.label,
              !!query.data?.current &&
                periodKey(p) === periodKey(query.data.current),
              scope ? progressOf(p) : null,
            )}
          </option>
        ))}
      </select>
    </div>
  );
};
